import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject, Input, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MessengerFileOpener, openableFile} from '../file-open';
import {MessageFile} from '../message-files';
import {
  formatVoiceSpeed, nextVoiceSpeed, VoiceClipRef, VoiceListen, VoicePlayer, VoicePlayerState, VoiceSpeed
} from '../voice-player.service';
import {formatVoiceLength} from '../voice-recording';
import {MessengerVoiceWaveformComponent} from '../voice-waveform/messenger-voice-waveform.component';

/**
 * A VOICE MESSAGE IN THE THREAD (ITP-E-249-01, Plan ITP-P-56 stories 9 and 10, ADR 0181): Play / Pause, a line that fills as
 * it plays, and its length, or the time so far while it plays. It plays through the tab's one player (`VoicePlayer`), so
 * starting it pauses any other clip, and it holds no playback state of its own: the thread re-creates its bubbles on every own
 * send, and a bubble drawn again reads where its clip stands from the player.
 *
 * A clip that could not be fetched says so and offers Try again; one this browser cannot play says so and offers Download,
 * through the one open action every chat file uses (`MessengerFileOpener`).
 *
 * ITP-E-249-02: the sender's Sending message is this bubble too, on the clip still on its way (`MessageFile.localUrl`), which
 * plays from memory; the sent message's bubble takes over on the same attachment id, so its playback carries on.
 *
 * ITP-E-249-04 (stories 9 and 10): the line is the clip's waveform, 48 bars from its peaks (`MessageFile.voicePeaks`, flat when it
 * has none) coloured to the play position (`MessengerVoiceWaveformComponent`). A tap or a drag on the bars, or an arrow key, seeks
 * (`VoicePlayer.seek`), before the first play too: the clip is fetched and plays from there. A small button cycles the tab's speed,
 * 1×, 1.5× and 2× (`VoicePlayer.setSpeed`).
 *
 * ITP-E-249-05 (story 11): someone else's voice message this user has not played shows the unplayed dot. The thread names the
 * message (`messageId`, `conversationId`); its clip goes to the player as a listen of it, so its first play is recorded and the dot
 * goes. One's own never shows the dot, and its plays are never recorded.
 */
@Component({
  standalone: true,
  selector: 'messenger-voice-bubble',
  imports: [CommonModule, MessengerVoiceWaveformComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './messenger-voice-bubble.component.html',
  styleUrls: ['./messenger-voice-bubble.component.scss']
})
export class MessengerVoiceBubbleComponent implements OnDestroy {
  private readonly player = inject(VoicePlayer);
  private readonly opener = inject(MessengerFileOpener);
  private readonly changes = inject(ChangeDetectorRef);
  private readonly fileChanged = new Subject<void>();

  @Input() set file(file: MessageFile) {
    this.fileChanged.next();
    this.current = file;
    this.length = formatVoiceLength(file?.voiceDurationMs);
    this.state$ = this.player.stateOf(file?.attachmentId);
    this.downloadFailed = false;
  }

  /** Whether the bubble is this user's own: the controls take the blue ground's ink. */
  @Input() @HostBinding('class.mine') mine = false;

  /** ITP-E-249-05: the voice message the bubble draws, and its conversation; null for one still on its way. */
  @Input() messageId: string | null = null;
  @Input() conversationId: string | null = null;

  current: MessageFile;
  /** The clip's length, `0:42`. */
  length = '0:00';
  state$: Observable<VoicePlayerState>;
  /** ITP-E-249-04: the speed every clip in the tab plays at. */
  readonly speed$: Observable<VoiceSpeed> = this.player.speed$;
  readonly formatSpeed = formatVoiceSpeed;
  /** ITP-E-249-05: the voice messages this user has played. */
  readonly played$: Observable<ReadonlySet<string>> = this.player.played$;
  /** Download was asked for and the file could not be opened. */
  downloadFailed = false;

  /** Play, or Pause while it plays. */
  toggle(): void {
    this.player.toggle(this.clip());
  }

  /** ITP-E-249-04: a tap, a drag or a key on the bars: the clip moves to `fraction` of its length, and plays from there if it was not. */
  seek(fraction: number): void {
    this.player.seek(fraction, this.clip());
  }

  /** ITP-E-249-04: the speed button: 1× → 1.5× → 2× → 1×, for every clip in the tab. */
  cycleSpeed(speed: number): void {
    this.player.setSpeed(nextVoiceSpeed(speed));
  }

  /** Try again, after the clip could not be fetched. */
  retry(): void {
    this.player.play(this.clip());
  }

  /** Download, when this browser cannot play the clip. */
  download(): void {
    this.downloadFailed = false;
    this.opener.open(openableFile(this.current)).pipe(takeUntil(this.fileChanged)).subscribe({
      error: () => {
        this.downloadFailed = true;
        this.changes.markForCheck();
      }
    });
  }

  /** Whether the button says Pause: the clip plays, or is on its way to. */
  isPlaying(state: VoicePlayerState): boolean {
    return state.status === 'playing' || state.status === 'loading';
  }

  /** The time so far while the clip plays or is paused part-way, else its length. */
  timeOf(state: VoicePlayerState): string {
    return state.status === 'playing' || (state.status === 'paused' && state.positionMs > 0)
      ? formatVoiceLength(state.positionMs) : this.length;
  }

  /** ITP-E-249-04: the clip's length, as the player has it or as the message says. */
  lengthOf(state: VoicePlayerState): number {
    return state.lengthMs || this.current?.voiceDurationMs || 0;
  }

  /** ITP-E-249-05: whether the unplayed dot shows: someone else's voice message this user has not played. */
  isUnplayed(played: ReadonlySet<string> | null): boolean {
    const listen = this.listen();
    return !!listen && !played?.has(listen.messageId);
  }

  /** "Play voice message, 0:42", or Pause while it plays. */
  labelOf(state: VoicePlayerState): string {
    return `${this.isPlaying(state) ? 'Pause' : 'Play'} voice message, ${this.length}`;
  }

  ngOnDestroy(): void {
    this.fileChanged.next();
    this.fileChanged.complete();
  }

  // ITP-E-249-02: a Sending bubble's clip carries its object URL, so it plays from memory with nothing fetched.
  private clip(): VoiceClipRef {
    const clip: VoiceClipRef = {attachmentId: this.current.attachmentId, name: this.current.name, durationMs: this.current.voiceDurationMs};
    if (this.current.localUrl) {
      clip.localUrl = this.current.localUrl;
    }
    const listen = this.listen();
    if (listen) {
      clip.listen = listen;
    }
    return clip;
  }

  // ITP-E-249-05: the message a play of the clip is a listen of; none for one's own, or for one the thread has not named.
  private listen(): VoiceListen | null {
    return !this.mine && this.messageId && this.conversationId ? {conversationId: this.conversationId, messageId: this.messageId} : null;
  }
}
